import React, { useEffect, useState } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Label,
} from "recharts";
import ThinScroll from "../../shared/ThinScroll";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { NeedsMultipleSyncs } from "./AbsorptionVelocityPanel";
import AbsorptionDatePicker from "./AbsorptionDatePicker";
import { getCiReportMarketAbsorption, ciGetAbsorptionDates } from "../../../api/competitiveIntelligence";
import { PanelTakeaway, fmtDate, fmtRate, fmtPct, SERIES_HUES } from "../shared";
import { axisProps, axisLabelStyle } from "../shared/chartTheme";

const PAGE_SIZE = 15;

// The market absorption RPC only returns rows for FUTURE p_check_in dates.
// Default the picker 2 weeks out so the panel shows data on first load,
// instead of following the global range filter (which is typically a past date).
const defaultFutureCheckIn = () => {
  const d = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
};

// Spec palette: available=blue (positive), sold-out=red (constraint/warning),
// rate=emerald (separate measure). Hues sourced from the shared SERIES_HUES.
const COLOR_AVAILABLE = SERIES_HUES.blue;
const COLOR_SOLDOUT = SERIES_HUES.red;
const COLOR_RATE = SERIES_HUES.emerald;

// Backend returns counts (competitors_sold_out / total_competitors); derive
// share % client-side so the 0–100 visualization comparable across clients
// with different comp set sizes.
const computeShares = (row) => {
  const total = Number(row.total_competitors) || 0;
  if (total <= 0) return { soldOutPct: null, availablePct: null };
  const soldOutPct = (Number(row.competitors_sold_out) || 0) / total * 100;
  return { soldOutPct, availablePct: 100 - soldOutPct };
};

const LegendSwatch = ({ color, label, kind }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
    {kind === "line" ? (
      <svg width="18" height="8" aria-hidden="true">
        <line x1="0" y1="4" x2="18" y2="4" stroke={color} strokeWidth="2.5" />
        <circle cx="9" cy="4" r="2.5" fill="#fff" stroke={color} strokeWidth="1.5" />
      </svg>
    ) : (
      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
    )}
    <span style={{ color }}>{label}</span>
  </span>
);

const MarketAbsorptionChart = ({ rows }) => {
  const data = rows.map((row) => {
    const { soldOutPct, availablePct } = computeShares(row);
    return {
      sync: row.sync_date,
      available: availablePct ?? 0,
      soldOut: soldOutPct ?? 0,
      avgRate: row.market_avg_rate ?? 0,
    };
  });
  return (
    <div>
    <div style={{ width: "100%", height: 240 }} className="rotate-x-labels">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 28 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
          <XAxis
            dataKey="sync"
            {...axisProps}
            tickFormatter={(date) => fmtDate(date)}
          />
          <YAxis
            yAxisId="pct"
            {...axisProps}
            tickFormatter={(percent) => `${percent}%`}
            domain={[0, 100]}
          >
            <Label
              value="Share (%)"
              angle={-90}
              position="insideLeft"
              offset={10}
              style={axisLabelStyle}
            />
          </YAxis>
          <YAxis
            yAxisId="rate"
            orientation="right"
            {...axisProps}
            tickFormatter={(rate) => `$${rate}`}
          >
            <Label
              value="Rate (USD)"
              angle={90}
              position="insideRight"
              offset={10}
              style={axisLabelStyle}
            />
          </YAxis>
          <ThemedChartTooltip
            labelFormatter={(date) => fmtDate(date)}
            formatter={(value, name) => (name === "Market Avg Rate" ? fmtRate(value) : fmtPct(value))}
            itemSorter={(item) => ({ available: 0, soldOut: 1, avgRate: 2 }[item.dataKey] ?? 99)}
          />
          <Area yAxisId="pct" type="monotone" dataKey="available" name="Available %" stroke={COLOR_AVAILABLE} fill={COLOR_AVAILABLE} fillOpacity={0.25} />
          <Area yAxisId="pct" type="monotone" dataKey="soldOut" name="Limited Avail. %" stroke={COLOR_SOLDOUT} fill={COLOR_SOLDOUT} fillOpacity={0.25} />
          <Line yAxisId="rate" type="monotone" dataKey="avgRate" name="Market Avg Rate" stroke={COLOR_RATE} strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-3">
      <LegendSwatch color={COLOR_AVAILABLE} label="Available %" />
      <LegendSwatch color={COLOR_SOLDOUT} label="Limited Avail. %" />
      <LegendSwatch color={COLOR_RATE} label="Market Avg Rate" kind="line" />
    </div>
    </div>
  );
};

const MarketAbsorptionTable = ({ rows }) => (
  <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
    <table className="w-full text-sm responsive-card-table">
      <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <th className="px-3 py-2.5 text-left font-semibold">Sync Date</th>
          <th className="px-3 py-2.5 text-right font-semibold">Available %</th>
          <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail. %</th>
          <th className="px-3 py-2.5 text-right font-semibold">Market Avg Rate</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const { soldOutPct, availablePct } = computeShares(row);
          return (
            <tr key={`${row.sync_date}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Sync Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDate(row.sync_date)}</td>
              <td data-label="Available %" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_AVAILABLE }}>{fmtPct(availablePct)}</td>
              <td data-label="Limited Avail. %" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_SOLDOUT }}>{fmtPct(soldOutPct)}</td>
              <td data-label="Market Avg Rate" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_RATE }}>{fmtRate(row.market_avg_rate)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </ThinScroll>
);

const MarketAbsorptionTablePaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <MarketAbsorptionTable rows={pageItems} />
      {totalPages > 1 && (
        <div className="pt-1 pb-1 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="syncs"
          />
        </div>
      )}
    </>
  );
};

const MarketAbsorptionBody = ({ data, dates, checkIn, onCheckInChange }) => {
  const rows = Array.isArray(data) ? data : [];
  // B3: surface the latest sync date as "Data as of …". Syncs land on real scrape
  // days (not the 1st), so this tells the user how fresh the series is.
  const asOf = !Array.isArray(data) ? data?.latest_sync_date : null;
  // Item 3 — AbsorptionDatePicker replaces the native <input type="date">. It
  // auto-selects the future check-in with the most syncs once `dates` resolve,
  // and hides itself when no pickable windows exist (the helper text covers that).
  const noDates = !Array.isArray(dates) || dates.length === 0;
  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
        <AbsorptionDatePicker dates={dates} selectedDate={checkIn} onChange={onCheckInChange} />
        {noDates && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Pick a check-in date to see how the wider market is filling for that night across syncs.
          </span>
        )}
      </div>
      {asOf && (
        <div className="px-4 pt-2 text-[11px] text-gray-400 dark:text-gray-500">
          Data as of <span className="font-medium">{fmtDate(asOf)}</span>
        </div>
      )}
      <div className="p-5 space-y-5">
        <MarketAbsorptionChart rows={rows} />
        <MarketAbsorptionTablePaginated rows={rows} />
      </div>
    </div>
  );
};

const MarketAbsorptionPanel = ({ clientId, categoryCode, onDateChange, say }) => {
  // Item 3 — the check-in date comes from ci_get_absorption_dates (the pickable
  // windows with sync history), not a computed future date. Start null and let
  // AbsorptionDatePicker auto-select the future date with the most syncs once the
  // dates resolve; fall back to a 2-weeks-out date when no pickable windows exist
  // so the report fetch still has a p_check_in to hit.
  const [checkIn, setCheckIn] = useState(null);
  const [absorptionDates, setAbsorptionDates] = useState([]);

  // Fetch the pickable check-in dates on mount/tab change with the selected
  // category. Reset `checkIn` first so the picker re-selects for the new
  // category's windows instead of holding a stale date.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setCheckIn(null);
    ciGetAbsorptionDates(clientId, categoryCode)
      .then((resp) => {
        if (cancelled) return;
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        setAbsorptionDates(rows);
        if (rows.length === 0) setCheckIn(defaultFutureCheckIn());
      })
      .catch(() => { if (!cancelled) setAbsorptionDates([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, categoryCode]);

  // Item 3 — lift the selected date to the page so the `say` builder can match
  // absorption insights views by `check_in` (see the page's say effect).
  const handleCheckInChange = (value) => {
    setCheckIn(value);
    onDateChange?.(value);
  };

  return (
    <AnalysisPanel
      id={`market-absorption-${clientId}-${categoryCode || "all"}-${checkIn || "none"}`}
      title="Market Absorption"
      tip="For a single check-in date: how the share of fully-booked competitors changes across syncs. Rising line = the market is compressing for that date — time to raise."
      description="Market-wide availability and limited-availability share for a single check-in date, tracked across syncs. Requires 2+ sync cycles."
      badge={categoryCode ? categoryCode.toUpperCase() : null}
      fetcher={() => (checkIn
        ? getCiReportMarketAbsorption(clientId, { checkIn, categoryCode: categoryCode || "rv" })
        : Promise.resolve({ data: [] }))}
      deps={[clientId, categoryCode, checkIn]}
    >
      {({ data }) => {
        // B3: check status before rendering. needs_multiple_syncs → show the RPC's
        // own message. ok (or a bare array) → render the chart + table body.
        if (data && !Array.isArray(data) && data.status === "needs_multiple_syncs") {
          return <NeedsMultipleSyncs status={data.status} message={data.message} currentSync={data.current_sync} latestSyncDate={data.latest_sync_date} />;
        }
        return (
          <>
            <PanelTakeaway panelKey="market_absorption" say={say} />
            <MarketAbsorptionBody data={data} dates={absorptionDates} checkIn={checkIn} onCheckInChange={handleCheckInChange} />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default MarketAbsorptionPanel;
